import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Activity,
  Eye,
  Zap,
  MessageCircle,
  Flame,
  ChevronDown,
  AlertCircle,
  Brain,
  Sparkles,
} from "lucide-react";

const IMG =
  "https://images.pexels.com/photos/33866372/pexels-photo-33866372.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const categories = {
  body: [
    {
      id: "motor",
      title: "Motor symptoms",
      icon: Activity,
      summary: "How the body moves — or refuses to.",
      description:
        "Motor symptoms can affect how your body moves. They might arrive without warning, last for minutes or days, and ease just as suddenly.",
      items: [
        "Paralysis — a limb or part of the body that simply stops working",
        "Tremors or shaking that come and go",
        "Sudden weakness",
        "A heavy, weighted feeling in your arms or legs",
        "Jerky movements",
        "Difficulty coordinating your steps",
      ],
    },
    {
      id: "sensory",
      title: "Sensory symptoms",
      icon: Eye,
      summary: "How the world arrives through your senses.",
      description:
        "Sensory symptoms involve changes in how you feel or process sensations — what your skin notices, what your eyes show you, how loud the world sounds. They can come and go without any warning.",
      items: [
        "Numbness, tingling, or a complete loss of feeling in certain areas",
        "Heightened sensitivity to noise",
        "Sensitivity to bright light",
        "Blurred vision",
        "Double vision",
      ],
    },
    {
      id: "seizures",
      title: "Functional seizures",
      icon: Zap,
      summary: "Episodes that look like epilepsy — but aren't.",
      description:
        "Functional seizures look very similar to epileptic seizures — full-body shaking, jerking, or collapsing — but they aren't caused by electrical activity in the brain. Even though they're not damaging, they can be frightening and exhausting.",
      items: [
        "Full-body tremors, jerking, or collapsing",
        "Often resemble epileptic seizures",
        "Not caused by electrical activity in the brain",
        "Can be triggered by stress, fatigue — or sometimes nothing obvious at all",
        "Not damaging to the brain, but exhausting afterwards",
      ],
    },
    {
      id: "fatigue",
      title: "Fatigue & pain",
      icon: Flame,
      summary: "A tiredness that a long sleep won't fix.",
      description:
        "Fatigue and pain are two of the most common symptoms. The fatigue can feel overwhelming, like your body has run completely out of energy. Pain often flares up after stress, activity, or a seizure.",
      items: [
        "Overwhelming fatigue, even after rest",
        "Headaches, sometimes persistent",
        "Muscle aches",
        "Joint pain",
        "Full-body soreness that comes and goes",
        "Flare-ups after stress, activity, or a seizure",
      ],
    },
  ],
  brain: [
    {
      id: "cognitive",
      title: "Speech & cognitive",
      icon: MessageCircle,
      summary: "When thoughts and words don't quite line up.",
      description:
        "Speech and cognitive symptoms affect how you think and communicate. They often surface during or after other symptoms — your brain working hard, but the wires pulling in different directions.",
      items: [
        "Brain fog",
        "Trouble concentrating",
        "Dizziness",
        "Slurred speech",
        "Temporary loss of speech",
        "Difficulty finding the right words",
        "Confusion",
      ],
    },
    {
      id: "perception",
      title: "Perception & wellbeing",
      icon: Brain,
      summary: "The quiet, internal weather of living with FND.",
      items: [
        "Nausea",
        "Disorientation during flares",
        "Feeling \"outside\" your own body",
      ],
    },
  ],
};

function SymptomCard({ cat, index }) {
  const [open, setOpen] = useState(false);
  const Icon = cat.icon;
  return (
    <button
      type="button"
      onClick={() => setOpen((s) => !s)}
      aria-expanded={open}
      data-testid={`symptom-card-${cat.id}`}
      className={`group w-full text-left rounded-3xl border bg-bone dark:bg-charcoal-light/10 p-7 lg:p-8 transition-all duration-500 ${
        open
          ? "border-terracotta/50 shadow-[0_20px_50px_-25px_rgba(200,121,102,0.45)]"
          : "border-bone-200 dark:border-charcoal-light/25 hover:border-charcoal/20"
      }`}
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <span
            className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-500 ${
              open
                ? "bg-terracotta text-bone"
                : "bg-terracotta/10 text-terracotta group-hover:bg-terracotta/20"
            }`}
          >
            <Icon size={20} strokeWidth={1.5} />
          </span>
          <div>
            <h3 className="font-serif text-2xl text-charcoal dark:text-bone tracking-tight">
              {cat.title}
            </h3>
            <p className="text-sm text-charcoal-light dark:text-bone/65 mt-1">{cat.summary}</p>
          </div>
        </div>
        <ChevronDown
          size={20}
          strokeWidth={1.5}
          className={`text-charcoal-light dark:text-bone/65 mt-3 transition-transform duration-500 ${
            open ? "rotate-180 text-terracotta" : ""
          }`}
        />
      </div>

      <div
        className={`grid transition-all duration-500 ease-out ${
          open ? "grid-rows-[1fr] opacity-100 mt-6" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="pt-5 border-t border-bone-200 dark:border-charcoal-light/25">
            {cat.description && (
              <p
                data-testid={`symptom-desc-${cat.id}`}
                className="text-base text-charcoal-light dark:text-bone/75 leading-relaxed mb-5"
              >
                {cat.description}
              </p>
            )}
            <ul className="space-y-2.5">
              {cat.items.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-base text-charcoal dark:text-bone"
                >
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function Symptoms() {
  return (
    <div data-testid="page-symptoms">
      {/* Header */}
      <section className="pt-16 lg:pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-8">
            <span className="w-6 h-px bg-terracotta" />
            Chapter Two
          </span>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <h1
              data-testid="symptoms-title"
              className="lg:col-span-8 font-serif text-charcoal dark:text-bone text-4xl sm:text-5xl lg:text-7xl leading-[1.03] tracking-tight text-balance"
            >
              Symptoms &
              <span className="italic text-terracotta"> types</span>.
            </h1>
            <p className="lg:col-span-4 text-lg text-charcoal-light dark:text-bone/65 leading-relaxed">
              Tap a card to expand it. Nothing here is medical advice — it’s
              a patient’s vocabulary, offered for recognition.
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimer callout */}
      <section className="pb-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div
            data-testid="disclaimer"
            className="flex flex-col md:flex-row gap-5 md:items-center rounded-3xl border border-moss/30 bg-moss/10 p-6 lg:p-8"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-moss/20 text-moss shrink-0">
              <Sparkles size={20} strokeWidth={1.5} />
            </span>
            <p className="text-base text-charcoal dark:text-bone leading-relaxed">
              <strong className="font-semibold">Written from experience — not a prescription.</strong>{" "}
              Every person with FND carries a different version of it. Use this
              page as a vocabulary, not a diagnosis.
            </p>
          </div>
        </div>
      </section>

      {/* Explorer with tabs */}
      <section className="pb-20 lg:pb-28" data-testid="symptom-explorer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-moss mb-6">
                  <span className="w-6 h-px bg-moss" />
                  Interactive explorer
                </span>
                <h2 className="font-serif text-3xl lg:text-4xl text-charcoal dark:text-bone leading-[1.08] tracking-tight text-balance">
                  Start with <em className="text-terracotta">body</em> or <em className="text-terracotta">brain</em>.
                </h2>
                <p className="mt-5 text-base text-charcoal-light dark:text-bone/65 leading-relaxed">
                  Switch tabs to move between the two. Each card opens into a
                  list of symptoms people often describe.
                </p>
                <div className="mt-8 relative hidden lg:block rounded-[2rem] overflow-hidden aspect-[4/5]">
                  <img
                    src={IMG}
                    alt="Fluid abstract design with smooth curves"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-8">
              <Tabs defaultValue="body" className="w-full" data-testid="symptom-tabs">
                <TabsList
                  data-testid="symptom-tabs-list"
                  className="inline-flex p-1 bg-bone-100 dark:bg-charcoal-light/20 rounded-full border border-bone-200 dark:border-charcoal-light/25 h-auto"
                >
                  <TabsTrigger
                    value="body"
                    data-testid="tab-body"
                    className="px-6 py-2.5 rounded-full text-sm font-medium text-charcoal-light dark:text-bone/65 data-[state=active]:bg-terracotta data-[state=active]:text-bone data-[state=active]:shadow-none transition-all"
                  >
                    Body
                  </TabsTrigger>
                  <TabsTrigger
                    value="brain"
                    data-testid="tab-brain"
                    className="px-6 py-2.5 rounded-full text-sm font-medium text-charcoal-light dark:text-bone/65 data-[state=active]:bg-terracotta data-[state=active]:text-bone data-[state=active]:shadow-none transition-all"
                  >
                    Brain
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="body" className="mt-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {categories.body.map((cat, i) => (
                      <SymptomCard key={cat.id} cat={cat} index={i} />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="brain" className="mt-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {categories.brain.map((cat, i) => (
                      <SymptomCard key={cat.id} cat={cat} index={i} />
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </section>

      {/* Variability */}
      <section className="py-20 lg:py-28 bg-bone-100/60 dark:bg-charcoal-light/10" data-testid="variability-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
            <span className="w-6 h-px bg-terracotta" />
            Symptom variability
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal dark:text-bone leading-[1.08] tracking-tight text-balance">
            No two people carry the <em className="text-terracotta">same FND</em>.
          </h2>
          <div className="mt-8 space-y-5 text-lg text-charcoal-light dark:text-bone/65 leading-relaxed max-w-3xl">
            <p>
              Every person with FND experiences it differently. Symptoms can
              vary widely from person to person — and even from day to day in
              the same person. A week of stillness can give way to a morning
              full of tremors. A clear Tuesday can blur by Thursday.
            </p>
            <p>
              There’s no single pattern. That’s part of what makes FND so
              hard to diagnose, and so hard to explain. Your version of FND
              is yours alone — isolating sometimes, but always worth listening to.
            </p>
          </div>
        </div>
      </section>

      {/* When to seek help */}
      <section className="py-20 lg:py-28" data-testid="seek-help-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="rounded-[2rem] bg-charcoal text-bone p-10 lg:p-14 relative overflow-hidden">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-terracotta/20 text-terracotta mb-6">
              <AlertCircle size={22} strokeWidth={1.5} />
            </div>
            <span className="block text-xs uppercase tracking-[0.28em] text-bone/60 mb-4">
              When to seek medical evaluation
            </span>
            <h2 className="font-serif text-bone text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-balance">
              If you’re scared, or can’t settle — <em className="text-terracotta">please, go</em>.
            </h2>
            <p className="mt-6 text-bone/80 text-lg leading-relaxed max-w-2xl">
              If your symptoms become overwhelming, frightening, or feel out
              of control, it’s a good idea to go to a doctor or hospital.
              Even if nothing dangerous is happening, getting checked can
              give you reassurance and help rule out other conditions. It’s
              always okay to seek help when you feel unsafe or unsure.
            </p>
          </div>
        </div>
      </section>

      {/* Closing note */}
      <section className="pb-24 lg:pb-32" data-testid="closing-note">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <p className="font-serif italic text-2xl sm:text-3xl text-charcoal dark:text-bone leading-snug text-balance">
            “Your body isn’t betraying you. It’s just listening to signals
            that got crossed on their way in.”
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.28em] text-charcoal-light">
            A note from the author
          </p>
        </div>
      </section>
    </div>
  );
}
